<template>
  <section class="digital-lecture-detail-video">
    <ul v-if="video.tags.length" class="list-category">
      <li v-for="tag in video.tags" :key="tag.id" class="item" @click="onTagClick(tag.id)">
        {{ tag.name }}
      </li>
    </ul>

    <h1 class="title">{{ video.name }}</h1>

    <div
      v-if="video.short_description"
      v-sane-html="video.short_description"
      class="description-video"
    />

    <iframe
      v-if="videoSrc"
      ref="iframeRef"
      :src="videoSrc"
      frameborder="0"
      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowfullscreen
      mozallowfullscreen
    />

    <div class="params">
      <div class="field -date">
        <BaseIconSvoi icon="time/calendar" />
        <span>{{ video.date_publication }}</span>
      </div>

      <button
        type="button"
        class="field -likes"
        :class="{ '-active': isLiked }"
        @click="onLikeClick"
      >
        <BaseIconSvoi icon="support/like-1" />
        <span>{{ likesCount }}</span>
      </button>

      <button type="button" class="field -share" @click="onShareClick">
        <BaseIconSvoi icon="essential/share-2" />
      </button>
    </div>
  </section>
</template>

<script lang="ts" setup>
  import { BaseIconSvoi } from '~/components/base-ui/icon'
  import type { DigitalLectureDetail } from '~/models/digital-lecture'
  import { useNotificationsStore } from '~/store/notifications'
  import { createVkVideoPlayer, getVkVideoUrl } from '~/shared/utils/pages/digital-lecture'
  import type { VkVideoPlayerController } from '~/shared/utils/pages/digital-lecture'

  const props = defineProps<{
    video: DigitalLectureDetail
    isLiked: boolean
    likesCount: number
  }>()

  const emit = defineEmits<{
    (e: 'like' | 'video-started' | 'video-ended'): void
  }>()

  const router = useRouter()
  const notifications = useNotificationsStore()

  const iframeRef = ref<HTMLIFrameElement | null>(null)
  let videoPlayerController: VkVideoPlayerController | null = null

  const videoSrc = computed(() => getVkVideoUrl(props.video.video_link || ''))

  const destroyPlayer = () => {
    videoPlayerController?.destroy()
    videoPlayerController = null
  }

  const initPlayer = async () => {
    if (!iframeRef.value || !videoSrc.value) {
      return
    }

    try {
      videoPlayerController = await createVkVideoPlayer({
        iframe: iframeRef.value,
        onStarted: () => emit('video-started'),
        onEnded: () => emit('video-ended'),
      })
    } catch {
      videoPlayerController = null
    }
  }

  onMounted(() => {
    initPlayer().catch(() => undefined)
  })

  onBeforeUnmount(destroyPlayer)

  const getShareUrl = () => {
    if (!import.meta.client) return ''
    return window.location.href
  }

  function onTagClick(id: number) {
    void router.push({ path: '/digital-lecture', query: { tags_id: String(id) } })
  }

  function onLikeClick() {
    if (props.isLiked) return
    emit('like')
  }

  async function onShareClick() {
    if (!navigator?.clipboard) return

    try {
      await navigator.clipboard.writeText(getShareUrl())
      notifications.pushInfo('Данные успешно скопированы в буфер обмена')
    } catch {
      notifications.pushError('Не удалось скопировать ссылку')
    }
  }
</script>

<style lang="scss" scoped>
  .digital-lecture-detail-video {
    padding: 24px 20px;
    border-radius: 32px;
    background-color: $color-default-white;

    @include vp-767 {
      padding: 24px 16px;
    }

    > .list-category {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      margin: 0 0 12px;
      padding: 0;
      list-style: none;

      @include vp-767 {
        flex-wrap: nowrap;
        overflow-x: auto;
        margin-bottom: 16px;
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
        }
      }

      > .item {
        padding: 8px 20px;
        font-size: 14px;
        line-height: 22px;
        border-radius: 12px;
        color: $color-potato-90;
        background-color: $color-figs-10;
        cursor: pointer;
        transition: opacity 0.2s ease;

        &:hover {
          opacity: 0.8;
        }

        @include vp-767 {
          font-size: 12px;
          line-height: 16px;
          flex-shrink: 0;
        }
      }
    }

    > .title {
      margin: 0 0 12px;
      font-size: 32px;
      font-weight: 500;
      line-height: 40px;

      @include vp-1279 {
        font-size: 28px;
        line-height: 36px;
      }

      @include vp-767 {
        margin-bottom: 16px;
        font-size: 24px;
        line-height: 32px;
      }
    }

    > .description-video {
      margin: 0 0 24px;
      font-size: 16px;
      line-height: 24px;
      overflow-wrap: break-word;

      :deep(a) {
        color: $color-kiwi-50;
        text-decoration: underline;
      }

      :deep(iframe) {
        width: 100%;
      }

      :deep(img) {
        width: 100% !important;
        height: auto !important;
        border-radius: 32px;

        @include vp-1279 {
          border-radius: 24px;
        }
      }

      :deep(ul) {
        list-style: none;
        @extend %flex-column;

        gap: 12px;
        padding: 16px;

        > li {
          position: relative;
          padding-left: 36px;
          font-size: 16px;
          line-height: 24px;

          &::before {
            position: absolute;
            content: '';
            top: calc(50% - 2px);
            left: 2px;
            width: 6px;
            height: 6px;
            background-color: #e6b400;
            border-radius: 50%;
          }
        }
      }

      :deep(ol) {
        list-style-position: inside;
        @extend %flex-column;

        gap: 12px;
        padding: 16px;

        > li {
          font-size: 16px;
          line-height: 24px;
        }
      }

      :deep(blockquote) {
        position: relative;
        margin: 0;
        padding: 16px 16px 16px 52px;
        font-size: 16px;
        line-height: 24px;
        color: #414242;

        &::before {
          position: absolute;
          content: '';
          top: 16px;
          left: 16px;
          width: 24px;
          height: 24px;
          background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEzLjA4NyA3LjkwNzI3QzEzLjA4NyA2Ljk2MDYgMTMuNzg3IDYuMTg3MyAxNC44MDcgNi4xODczTDE2Ljk0NyA2LjE4NzNDMTcuODkzNyA2LjE4NzMgMTguNjY3IDYuOTYwNiAxOC42NjcgNy45MDcyN0wxOC42NjcgMTEuNzczOSIgc3Ryb2tlPSIjRTZCNDAwIiBzdHJva2Utd2lkdGg9IjEuNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGQ9Ik0xOC42NjcgMTEuNzY3NUwxNC44MDAzIDExLjc2NzVDMTQuMTYwMyAxMS43Njc1IDEzLjY1MzcgMTEuNDY3NCAxMy4zNjAzIDExLjAwNzQiIHN0cm9rZT0iI0U2QjQwMCIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8cGF0aCBkPSJNMTguNjY3IDExLjc2NzJDMTguNjY3IDE1LjgwMDUgMTcuOTEzNyAxNi40NjcyIDE1LjY0NyAxNy44MTM4IiBzdHJva2U9IiNFNkI0MDAiIHN0cm9rZS13aWR0aD0iMS41IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHBhdGggZD0iTTEwLjkxMzMgMTEuNzY3M0w3LjA0NjU5IDExLjc2NzNDNi4wMjY1OSAxMS43NjczIDUuMzI2NTkgMTAuOTk0IDUuMzI2NTkgMTAuMDQ3M0w1LjMyNjU5IDcuOTAwNjNDNS4zMjY1OSA2Ljk1Mzk3IDYuMDI2NTkgNi4xODA2NiA3LjA0NjU5IDYuMTgwNjZMOS4xOTk5MiA2LjE4MDY2QzEwLjE0NjYgNi4xODA2NiAxMC45MTk5IDYuOTUzOTcgMTAuOTE5OSA3LjkwMDYzTDEwLjkxOTkgMTEuNzY3MyIgc3Ryb2tlPSIjRTZCNDAwIiBzdHJva2Utd2lkdGg9IjEuNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGQ9Ik0xMC45MTMxIDExLjc2N0MxMC45MTMxIDE1LjgwMDMgMTAuMTU5OCAxNi40NjY5IDcuODkzMDkgMTcuODEzNiIgc3Ryb2tlPSIjRTZCNDAwIiBzdHJva2Utd2lkdGg9IjEuNjY2NjEiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K');
          background-repeat: no-repeat;
        }
      }

      :deep(table) {
        width: 100% !important;
        border-collapse: collapse;
        margin: 16px 0;
        font-size: 16px;
        line-height: 24px;
        background-color: $color-default-white;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgb(0 0 0 / 8%);

        @include vp-767 {
          font-size: 14px;
          line-height: 22px;
          margin: 12px 0;
        }

        td,
        th {
          border: 1px solid #e5e7eb;
          padding: 12px 16px;
          text-align: left;
          vertical-align: top;
          overflow-wrap: break-word;

          @include vp-767 {
            padding: 8px 12px;
          }
        }

        th {
          background-color: #f9fafb;
          color: #374151;
        }

        tbody tr:hover {
          background-color: #f3f4f6;
        }

        &.oc-dashed-borders {
          td:first-child {
            width: 80px;
            min-width: 80px;
            text-align: center;
            white-space: nowrap;
          }

          td:last-child {
            width: auto;
          }
        }

        &:not(.oc-dashed-borders) {
          td:first-child {
            width: 60px;
            min-width: 60px;
            text-align: center;
          }

          td:nth-child(2) {
            width: 120px;
            min-width: 120px;
          }

          td:last-child {
            width: auto;
          }
        }
      }
    }

    > iframe {
      width: 100%;
      height: 450px;
      margin-bottom: 24px;
      border: 0;
      border-radius: 32px;

      @include vp-1279 {
        height: 368px;
      }

      @include vp-767 {
        height: 200px;
        border-radius: 20px;
      }
    }

    > .params {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;

      > .field {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        line-height: 16px;
        color: $color-potato-70;
        background: transparent;
        border: 0;
        padding: 0;
        font-family: inherit;

        :deep(.svoi-icon) {
          width: 20px;
          min-width: 20px;
          height: 20px;
          min-height: 20px;
        }

        &.-likes {
          margin-left: auto;
          color: $color-default-black;
          cursor: pointer;
        }

        &.-share {
          color: $color-default-black;
          cursor: pointer;
        }

        &.-active {
          color: $color-kiwi-50;
        }
      }
    }
  }
</style>
